import { useState } from 'react';
import { Copy } from 'lucide-react';
import { toast } from 'sonner';
import { Badge, Button, Dialog, Field, Input, Select, type Tone } from '@zt/ui';
import { ConfirmButton } from '../components/Confirm';
import { api, IdempotencyKey } from '../lib/api';
import { HelpTip } from '../lib/help';
import { useI18n, useT } from '../lib/i18n';
import { useMoney } from '../lib/money';
import { useApiMutation } from '../lib/mutation';
import { usePermissions } from '../lib/session';
import type { OrderDetail } from '../lib/types';

/**
 * Ödeme linki (F5B-13, ADR-069): siparişin ödeme bölümünde bağlantılar, oluşturma ve iptal. Bağlantı yalnızca
 * oluşturulduğu anda gösterilir (sunucuda yalnızca özeti durur); kaybolursa yenisi oluşturulur.
 */

const STATE_TONE: Record<string, Tone> = {
  active: 'pine',
  paid: 'moss',
  expired: 'slate',
  cancelled: 'slate',
  closed: 'neutral',
  amount_changed: 'amber',
};

const HOURS = [24, 72, 168, 720] as const;

interface Created {
  url: string;
  expiresAt: string;
  emailed: boolean;
}

export function PaymentLinksBlock({ order }: { order: OrderDetail }) {
  const t = useT();
  const money = useMoney();
  const { formatDate } = useI18n();
  const { can } = usePermissions();
  const [open, setOpen] = useState(false);
  const [created, setCreated] = useState<Created | null>(null);
  const canWrite = can('orders.order.write');
  const cancel = useApiMutation((id: string) => api.post(`/v1/admin/payment-links/${id}/cancel`), {
    invalidate: [['order', order.id]],
    success: t('admin.paymentLinks.cancelledToast'),
  });
  const links = order.paymentLinks;
  if (!links.length && !(order.canPaymentLink && canWrite)) return null;
  const hasActive = links.some((l) => l.state === 'active');
  return (
    <div className="mt-4">
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <p className="text-sm font-medium">{t('admin.paymentLinks.title')}</p>
        {order.canPaymentLink && canWrite && (
          <span className="inline-flex items-center gap-1">
            <Button size="sm" onClick={() => setOpen(true)}>
              {hasActive ? t('admin.paymentLinks.renew') : t('admin.paymentLinks.create')}
            </Button>
            <HelpTip k="orders.payment_link" />
          </span>
        )}
      </div>
      {links.length > 0 ? (
        <ul className="divide-y divide-line rounded-md border border-line bg-surface text-sm">
          {links.map((l) => (
            <li key={l.id} className="flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-2.5">
              <div className="min-w-0 flex-1">
                <p>
                  {t('admin.paymentLinks.createdAt', { date: formatDate(l.createdAt, true) })} ·{' '}
                  <span className="tabular">{money(l.amountMinor, l.currency)}</span>
                </p>
                <p className="text-ink-3">
                  {l.state === 'paid' && l.paidAt
                    ? t('admin.paymentLinks.paidAt', { date: formatDate(l.paidAt, true) })
                    : t('admin.paymentLinks.expiresAt', { date: formatDate(l.expiresAt, true) })}
                  {' · '}
                  {l.openedAt
                    ? t('admin.paymentLinks.openedAt', { date: formatDate(l.openedAt, true) })
                    : t('admin.paymentLinks.notOpened')}
                </p>
              </div>
              <Badge tone={STATE_TONE[l.state] ?? 'neutral'}>
                {t(`admin.paymentLinks.states.${l.state}`)}
              </Badge>
              {l.state === 'active' && canWrite && (
                <ConfirmButton
                  variant="ghost"
                  message={t('admin.paymentLinks.cancelConfirm')}
                  onConfirm={() => cancel.mutateAsync(l.id)}
                >
                  {t('admin.paymentLinks.cancel')}
                </ConfirmButton>
              )}
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-ink-3">{t('admin.paymentLinks.empty')}</p>
      )}
      {open && (
        <CreateLinkDialog
          order={order}
          onClose={() => setOpen(false)}
          onCreated={(c) => {
            setOpen(false);
            setCreated(c);
          }}
        />
      )}
      {created && <LinkCreatedDialog created={created} onClose={() => setCreated(null)} />}
    </div>
  );
}

function CreateLinkDialog({
  order,
  onClose,
  onCreated,
}: {
  order: OrderDetail;
  onClose: () => void;
  onCreated: (c: Created) => void;
}) {
  const t = useT();
  const money = useMoney();
  const [hours, setHours] = useState<number>(72);
  const [sendEmail, setSendEmail] = useState(false);
  const [idem] = useState(() => new IdempotencyKey());
  const create = useApiMutation(
    () =>
      api.post<Created>(
        `/v1/admin/orders/${order.id}/payment-links`,
        { hours, sendEmail },
        { idempotencyKey: idem },
      ),
    {
      invalidate: [['order', order.id], ['orders']],
      onSuccess: (r) => onCreated(r),
    },
  );
  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose()}
      title={t('admin.paymentLinks.createTitle')}
      description={t('admin.paymentLinks.createLead', { amount: money(order.totalMinor, order.currency) })}
      closeLabel={t('common.close')}
      footer={
        <>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button variant="primary" loading={create.isPending} onClick={() => create.mutate(undefined)}>
            {t('admin.paymentLinks.createButton')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {order.status === 'draft' && (
          <p className="text-sm text-amber">{t('admin.paymentLinks.draftNote')}</p>
        )}
        {order.paymentLinks.some((l) => l.state === 'active') && (
          <p className="text-sm text-ink-2">{t('admin.paymentLinks.renewNote')}</p>
        )}
        <Field label={t('admin.paymentLinks.hours')} help={<HelpTip k="orders.payment_link_hours" />}>
          {(p) => (
            <Select {...p} value={hours} onChange={(e) => setHours(Number(e.target.value))}>
              {HOURS.map((h) => (
                <option key={h} value={h}>
                  {t(`admin.paymentLinks.hourOptions.h${h}`)}
                </option>
              ))}
            </Select>
          )}
        </Field>
        {order.email ? (
          <div className="flex items-start gap-2">
            <input
              id="payment-link-email"
              type="checkbox"
              className="mt-1 accent-[var(--zt-pine)]"
              checked={sendEmail}
              onChange={(e) => setSendEmail(e.target.checked)}
            />
            <label htmlFor="payment-link-email" className="text-sm">
              {t('admin.paymentLinks.sendEmail', { email: order.email })}
            </label>
            <HelpTip k="orders.payment_link_email" />
          </div>
        ) : (
          <p className="text-sm text-ink-3">{t('admin.paymentLinks.noEmail')}</p>
        )}
      </div>
    </Dialog>
  );
}

function LinkCreatedDialog({ created, onClose }: { created: Created; onClose: () => void }) {
  const t = useT();
  const { formatDate } = useI18n();
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(created.url);
      toast.success(t('admin.paymentLinks.copied'));
    } catch {
      toast.error(t('admin.paymentLinks.copyFailed'));
    }
  };
  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose()}
      title={t('admin.paymentLinks.createdTitle')}
      description={t('admin.paymentLinks.createdLead')}
      closeLabel={t('common.close')}
      footer={
        <Button variant="primary" onClick={onClose}>
          {t('common.close')}
        </Button>
      }
    >
      <div className="space-y-3">
        <Field label={t('admin.paymentLinks.url')}>
          {(p) => <Input {...p} readOnly value={created.url} onFocus={(e) => e.target.select()} />}
        </Field>
        <Button onClick={() => void copy()}>
          <Copy className="h-4 w-4" aria-hidden />
          {t('admin.paymentLinks.copy')}
        </Button>
        <p className="text-sm text-ink-2">
          {t('admin.paymentLinks.expiresAt', { date: formatDate(created.expiresAt, true) })}
        </p>
        <p className="text-sm text-ink-2" role="status">
          {created.emailed ? t('admin.paymentLinks.emailed') : t('admin.paymentLinks.notEmailed')}
        </p>
      </div>
    </Dialog>
  );
}
